import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUpload from "../../components/ImageUpload";

const CATS = [
  { v: "danismanlik", l: "Danışmanlık" },
  { v: "mevzuat", l: "Mevzuat" },
  { v: "yildiz", l: "Yıldız Standartları" },
  { v: "secim", l: "Ekipman Seçimi" },
  { v: "duzenleme", l: "Oda Düzenleme" },
  { v: "trend", l: "Sektör Trendleri" },
];

const BLANK = {
  slug: "", title_tr: "", title_en: "", title_de: "",
  excerpt: "", content: "", cover_image: "", category: "danismanlik",
  author: "Otel Pro Danışmanlık", tags: [],
  seo_title: "", seo_description: "", published_at: "", active: true,
};

export default function AdminBlog() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/admin/blog").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editingId) await api.put(`/admin/blog/${editingId}`, body);
      else await api.post("/admin/blog", body);
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading-serif text-3xl font-semibold text-navy">Blog</h1>
        <Button onClick={() => { setForm(BLANK); setEditingId(null); setOpen(true); }} className="bg-gold hover:bg-[#c49d58]" data-testid="admin-blog-new">
          <Plus size={16} className="mr-1" /> Yeni Yazı
        </Button>
      </div>

      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <table className="w-full text-sm">
          <thead className="bg-softbg text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left p-3">Başlık</th>
              <th className="text-left p-3">Kategori</th>
              <th className="text-left p-3">Yayın</th>
              <th className="text-center p-3">Aktif</th>
              <th className="text-right p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="p-3">
                  <div className="font-medium text-navy">{r.title_tr}</div>
                  <div className="text-[11px] text-gray-400">/{r.slug}</div>
                </td>
                <td className="p-3 text-gray-600">{CATS.find((c) => c.v === r.category)?.l || r.category}</td>
                <td className="p-3 text-xs text-gray-500">{(r.published_at || "").slice(0, 10)}</td>
                <td className="p-3 text-center">{r.active ? "●" : "○"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => { setForm({ ...BLANK, ...r }); setEditingId(r.id); setOpen(true); }} className="p-1.5 text-gray-500 hover:text-teal"><Pencil size={14} /></button>
                  <button onClick={() => del(r.id)} className="p-1.5 text-gray-500 hover:text-rose-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto admin-scroll">
          <DialogHeader><DialogTitle className="heading-serif">{editingId ? "Yazıyı Düzenle" : "Yeni Yazı"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required /></div>
              <div><Label className="text-xs">Kategori</Label>
                <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
            </div>
            <div><Label className="text-xs">Başlık (TR)</Label><Input value={form.title_tr} onChange={(e) => setForm({ ...form, title_tr: e.target.value })} required /></div>
            <div><Label className="text-xs">Özet</Label><Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
            <div><Label className="text-xs">İçerik</Label><RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <ImageUpload value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} label="Kapak Görseli" testId="blog-cover" />
              <div><Label className="text-xs">Yazar</Label><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Etiketler (virgülle)</Label><Input value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">SEO Başlık</Label><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></div>
              <div><Label className="text-xs">SEO Açıklama</Label><Input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif</label>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-gold hover:bg-[#c49d58]">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
